import React, { useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import Helmet from 'react-helmet';
import Facebook from './Facebook';
import Twitter from './Twitter';
import useDarkMode from '../../../hooks/useDarkMode';

// Complete tutorial: https://www.gatsbyjs.org/docs/add-seo-component/

const SEO = ({ title, desc, banner, schema, location, article, node }) => {
  const staticData = useStaticQuery(query);

  const { data } = useMergePrismicPreviewData(staticData);
  const { site, prismicSettings } = data;

  const { data: settingsData } = prismicSettings;
  const { total_reviews: totalReviews, review_score: reviewScore } = settingsData;

  const { pathname } = location;

  const {
    buildTime,
    siteMetadata: {
      siteUrl,
      defaultTitle,
      defaultDescription,
      defaultBanner,
      siteName,
      siteLanguage,
      ogLanguage,
      author,
      twitter,
    },
  } = site;

  const darkMode = useDarkMode();

  useEffect(() => {
    const favicon = document.querySelectorAll('[rel="icon"]')[0];
    if (darkMode) {
      if (favicon) favicon.href = '/favicon-dark-mode.png';
    }
  }, [darkMode]);

  const siteOgImage = `${siteUrl}${defaultBanner}`;
  // Page banners are authored as site-relative paths; social scrapers need an absolute URL.
  const pageOgImage = banner && banner.startsWith('/') ? `${siteUrl}${banner}` : banner;
  const ogImage = pageOgImage || siteOgImage;

  const seo = {
    title: title || defaultTitle,
    description: desc || defaultDescription,
    image: ogImage,
    url: `${siteUrl}${pathname || ''}`,
  };

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  // You can fill out the 'author', 'creator' with more data or another type (e.g. 'Organization')

  const schemaOrgWebPage = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    url: siteUrl,
    headline: seo.title,
    inLanguage: siteLanguage,
    mainEntityOfPage: siteUrl,
    description: seo.description,
    name: siteName,
    author: {
      '@type': 'Person',
      name: author,
    },
    copyrightHolder: {
      '@type': 'Person',
      name: author,
    },
    copyrightYear: '2021',
    creator: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Person',
      name: author,
    },
    datePublished: '2019-01-18T10:30:00+01:00',
    dateModified: buildTime,
    image: {
      '@type': 'ImageObject',
      url: ogImage,
    },
  };

  // Initial breadcrumb list
  const itemListElement = [
    {
      '@type': 'ListItem',
      item: {
        '@id': siteUrl,
        name: 'Homepage',
      },
      position: 1,
    },
  ];

  let schemaArticle = null;

  if (article) {
    schemaArticle = {
      '@context': 'http://schema.org',
      '@type': 'Article',
      author: {
        '@type': 'Person',
        name: author,
      },
      copyrightHolder: {
        '@type': 'Person',
        name: author,
      },
      copyrightYear: '2021',
      creator: {
        '@type': 'Person',
        name: author,
      },
      publisher: {
        '@type': 'Organization',
        name: author,
        logo: {
          '@type': 'ImageObject',
          url: siteOgImage,
        },
      },
      datePublished: node.first_publication_date,
      dateModified: node.last_publication_date,
      description: seo.description,
      headline: seo.title,
      inLanguage: 'en',
      url: seo.url,
      name: seo.title,
      image: {
        '@type': 'ImageObject',
        url: seo.image,
      },
      mainEntityOfPage: seo.url,
    };
    // Push current blogpost into breadcrumb list
    itemListElement.push({
      '@type': 'ListItem',
      item: {
        '@id': seo.url,
        name: seo.title,
      },
      position: 2,
    });
  }

  const schemaOrgProduct = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: 'Driva',
    description: seo?.description,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: reviewScore || 4.9,
      reviewCount: totalReviews || 828,
    },
  };

  const schemaBusinessAndSiteLinks = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    url: 'https://www.driva.com.au/',
    sameAs: ['https://www.facebook.com/drivaau/', 'https://www.instagram.com/driva_au/'],
    name: 'Driva',
    logo: {
      '@type': 'ImageObject',
      url: siteOgImage,
    },
  };

  const excludeProductSchema = pathname.includes('blog/') || pathname.includes('lender/');

  // Hreflang alternates for en-AU, en-GB, en-NZ (same path on all three domains)
  let pathWithSlash = '/';
  if (pathname && pathname !== '/') {
    pathWithSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
  }

  return (
    <>
      <Helmet title={seo.title}>
        <html lang={siteLanguage} />
        <meta name="description" content={seo.description} />
        <meta name="image" content={seo.image} />
        <meta name="driva" content="Driva" />
        {/* Hreflang alternates for all pages on driva.com.au, drivafinance.co.uk, driva.co.nz */}
        <link rel="alternate" hrefLang="en-AU" href={`https://driva.com.au${pathWithSlash}`} />
        <link rel="alternate" hrefLang="en-GB" href={`https://drivafinance.co.uk${pathWithSlash}`} />
        <link rel="alternate" hrefLang="en-NZ" href={`https://driva.co.nz${pathWithSlash}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://driva.com.au${pathWithSlash}`} />
        {/* Insert schema.org data conditionally (webpage/article) + everytime (breadcrumbs) */}
        {!article && <script type="application/ld+json">{JSON.stringify(schema || schemaOrgWebPage)}</script>}
        {article && <script type="application/ld+json">{JSON.stringify(schema || schemaArticle)}</script>}
        {!excludeProductSchema && <script type="application/ld+json">{JSON.stringify(schemaOrgProduct)}</script>}
        <script type="application/ld+json">{JSON.stringify(schemaBusinessAndSiteLinks)}</script>
        <link rel="icon" type="image/png" href="/favicon-light-mode.png" />
      </Helmet>
      <Facebook
        desc={seo.description}
        image={seo.image}
        title={seo.title}
        type={article ? 'article' : 'website'}
        url={seo.url}
        locale={ogLanguage}
        name={siteName}
      />
      <Twitter title={seo.title} image={seo.image} desc={seo.description} username={twitter} />
    </>
  );
};

export default SEO;

const query = graphql`
  query SEO {
    site {
      buildTime(formatString: "YYYY-MM-DD")
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        defaultBanner: banner
        siteUrl
        siteName
        siteLanguage
        ogLanguage
        author
        twitter
      }
    }
    prismicSettings {
      data {
        review_score
        total_reviews
      }
    }
  }
`;
